import { useCallback, useState } from "react"

import type { SaleProp } from "@/modules/types/jobs/affiliate"

export interface ProductCampaignItem {
  id?: string
  title?: string
  thumbnail?: string
  saleProps?: SaleProp[]
}

export interface SelectedSku {
  productId: string
  propId: string
  propValueId: string
}

export function useMultipleSkuSelection() {
  // State: { [productId]: { [propId]: propValueId } }
  const [selectedSkus, setSelectedSkus] = useState<Record<string, Record<string, string>>>({})

  const togglePropValue = useCallback((productId: string, propId: string, propValueId: string) => {
    setSelectedSkus((prev) => {
      const productSelections = prev[productId] || {}
      const isCurrentlySelected = productSelections[propId] === propValueId

      if (isCurrentlySelected) {
        // Unselect
        const { [propId]: _, ...rest } = productSelections
        if (Object.keys(rest).length === 0) {
          const { [productId]: __, ...restProducts } = prev
          return restProducts
        }
        return { ...prev, [productId]: rest }
      } else {
        // Select
        return {
          ...prev,
          [productId]: {
            ...productSelections,
            [propId]: propValueId
          }
        }
      }
    })
  }, [])

  const isSelected = useCallback(
    (productId: string, propId: string, propValueId: string) => {
      return selectedSkus[productId]?.[propId] === propValueId
    },
    [selectedSkus]
  )

  const getSelectedSkusArray = useCallback((): SelectedSku[] => {
    const result: SelectedSku[] = []
    Object.entries(selectedSkus).forEach(([productId, propMap]) => {
      Object.entries(propMap).forEach(([propId, propValueId]) => {
        result.push({ productId, propId, propValueId })
      })
    })
    return result
  }, [selectedSkus])

  const isProductComplete = useCallback(
    (productId: string, requiredPropCount: number) => {
      const productSelections = selectedSkus[productId]
      return productSelections && Object.keys(productSelections).length === requiredPropCount
    },
    [selectedSkus]
  )

  const getProductSelections = useCallback(
    (productId: string) => {
      return selectedSkus[productId] || {}
    },
    [selectedSkus]
  )

  return {
    selectedSkus,
    togglePropValue,
    isSelected,
    getSelectedSkusArray,
    isProductComplete,
    getProductSelections,
    setSelectedSkus
  }
}
